import { Box2d, Editor, TLAnimationOptions, VecLike, clamp, useEditor } from '@tldraw/tldraw'
import { useLayoutEffect } from 'react'
import { compact } from 'lodash'
import { getOrderedFrames } from 'components/VisualStudioPlayground/Shapes/FrameShape/helpers'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { getCaptionPadding } from 'components/Design/utils/getInitialSceneBounds'

/**
 * This component overrides the default tools behavior of tldraw.
 * If you want to override the default behavior of tldraw, you can do so here.
 */
export const DefaultToolsOverrides = () => {
  const editor = useEditor()
  const { isMobile } = useResponsiveDesign()

  useLayoutEffect(() => {
    disableDoubleClickOnCanvas(editor)
  }, [editor, isMobile])

  return null
}

// This is a hack to disable the double click on canvas behavior baked into tldraw.
// Currently, the only way to disable this behavior is to override the select.idle state at runtime.
function disableDoubleClickOnCanvas(editor: Editor) {
  const selectIdle = editor.getStateDescendant('select.idle')
  if (selectIdle && 'handleDoubleClickOnCanvas' in selectIdle) {
    selectIdle.handleDoubleClickOnCanvas = () => {}
  }
}

export function clampCamera(
  point: { x: number; y: number; z: number },
  designBounds: Box2d,
  fullSizeViewport: Box2d,
  isMobile: boolean = false,
  captionPadding: { top: number; bottom: number },
) {
  let { x, y, z } = point
  const MARGIN = isMobile ? 300 : 200
  const scaledViewport = fullSizeViewport.scale(z)

  if (!isMobile && designBounds.minX >= scaledViewport.minX && designBounds.maxX <= scaledViewport.maxX) {
    x = (scaledViewport.width - designBounds.width) / 2
  } else {
    const leftX = MARGIN / z
    const rightX =
      scaledViewport.width > designBounds.width
        ? (scaledViewport.width - designBounds.width) / 2
        : -designBounds.width + fullSizeViewport.width
    x = Math.min(x, leftX)
    x = Math.max(x, rightX - MARGIN / z)
  }

  if (!isMobile && designBounds.minY >= scaledViewport.minY && designBounds.maxY <= scaledViewport.maxY) {
    // Center the design vertically if it fits in the viewport. Since minY can
    // be negative due to the padding top, and the camera has inverted
    // coordinates (positive y is down), we need to add it twice to get the
    // correct offset.
    y = (scaledViewport.height - designBounds.height - designBounds.minY * 2) / 2
  } else {
    const topY = -designBounds.minY
    const bottomY =
      scaledViewport.height > designBounds.height
        ? (scaledViewport.height - designBounds.height) / 2
        : -designBounds.height + fullSizeViewport.height
    y = Math.min(y, topY + MARGIN / z)
    y = Math.max(y, bottomY - MARGIN / z)
  }

  return { x, y, z }
}

export function setCameraBoundaries(
  editor: Editor,
  padding: { top: number; bottom: number },
  isMobile: boolean = false,
  cb?: (editor: Editor) => void,
) {
  const captionPadding = getCaptionPadding()

  editor.setCamera = (point: VecLike, animation?: TLAnimationOptions): Editor => {
    let x = Number.isFinite(point.x) ? point.x : 0
    let y = Number.isFinite(point.y) ? point.y : 0
    const z = point.z && Number.isFinite(point.z) ? point.z : editor.getZoomLevel()

    const frames = getOrderedFrames(editor)
    if (frames.length === 0) return editor
    const currentViewport = editor.getViewportPageBounds()
    const fullSizeViewport = currentViewport.clone().scale(1 / editor.getZoomLevel())
    const designBounds = Box2d.Common(
      compact(
        frames.map((frame) => {
          const box = editor.getShapePageBounds(frame.id)

          if (frame.props.position === 1 && box && !isMobile) {
            return new Box2d(box.x, box.y - padding.top, box.width, box.height + padding.top + padding.bottom)
          }

          return box
        }),
      ),
    )

    const target = clampCamera({ x, y, z }, designBounds, fullSizeViewport, isMobile, captionPadding)

    if (animation) {
      const { width, height } = editor.getViewportScreenBounds()
      // @ts-ignore
      editor._animateToViewport(new Box2d(-target.x, -target.y, width / target.z, height / target.z), animation)
    } else {
      // @ts-ignore
      editor._setCamera(target, animation)
    }

    if (isMobile) {
      editor.updateInstanceState({ canMoveCamera: false })
    }

    return editor
  }

  editor.store.listen((entry) => {
    if ('instance:instance' in entry.changes.updated) {
      editor.setCamera(editor.getCamera())
    }
  })

  const MIN_ZOOM = 0.1
  const MAX_ZOOM = 8

  editor.zoomToBounds = (bounds, targetZoom, animation) => {
    if (!editor.getInstanceState().canMoveCamera) return editor

    const viewportScreenBounds = editor.getViewportScreenBounds()

    const inset = Math.min(120, viewportScreenBounds.width * 0.12)

    let zoom = clamp(
      Math.min(
        (viewportScreenBounds.width - inset) / bounds.width,
        (viewportScreenBounds.height - inset) / bounds.height,
      ),
      MIN_ZOOM,
      MAX_ZOOM,
    )

    if (targetZoom !== undefined) {
      zoom = Math.min(targetZoom, zoom)
    }

    editor.setCamera(
      {
        x: -bounds.minX + (viewportScreenBounds.width - bounds.width * zoom) / 2 / zoom,
        y: -bounds.minY + (viewportScreenBounds.height - bounds.height * zoom) / 2 / zoom,
        z: zoom,
      },
      animation,
    )

    return editor
  }
  cb?.(editor)
}
